import {useEffect, useMemo, useState} from "react";
import _ from "lodash";
import {useAtomValue, useSetAtom} from "jotai";
import {useTranslation} from "react-i18next";
import {Box, Button, Stack, Typography} from "@mui/material";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {getUserCredentials, rotateUserCredentials} from "../../services/userCredentialsService";
import {isResourceLoading, isResourceSucceeded} from "../../utils/resourceUtils";
import {ApiCredentialsCard} from "../users/ApiCredentialsCard";
import {swallowException} from "../../utils/appUtils";
import {UserProfileCard} from "./UserProfileCard";
import {UserEditDialog} from "../dialogs/UserEditDialog";
import {activeUserAtom, activeUserLoadable} from "../../store/activeUserStore";
import {UserPreferencesCard} from "./UserPreferencesCard";
import {EditIcon} from "../icons/EditIcon";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";

export const UserDetailsView: React.FC = () => {
    const {t} = useTranslation();
    const [userEditDialogOpened, setUserEditDialogOpened] = useState<boolean>(false);
    const userState = useAtomValue(activeUserLoadable);
    const user = userState.state === "hasData" && userState.data !== undefined ? userState.data : undefined;
    const credentialsManagement = useResourceManagement({getUserCredentials, rotateUserCredentials});
    const {getUserCredentials: getCredentials} = credentialsManagement;
    const refreshUser = useSetAtom(activeUserAtom);
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const credentials = useMemo(
        () => (isResourceSucceeded(credentialsManagement.resource) ? credentialsManagement.resource.data : undefined),
        [credentialsManagement],
    );

    useEffect(() => {
        if (user && access.canViewUserApiCredentials) {
            getCredentials(user.id).catch(swallowException);
        }
    }, [user, getCredentials, access]);

    const onRotateCredentials = () => {
        credentialsManagement.reset();
        if (user) {
            credentialsManagement.rotateUserCredentials(user.id).catch(swallowException);
        }
    };

    const onUserEditDialogClose = (isSuccessful: boolean) => {
        setUserEditDialogOpened(false);
        if (isSuccessful) {
            refreshUser();
        }
    };

    if (user !== undefined) {
        return (
            <>
                <Stack direction="column" gap="1.5rem">
                    <Stack direction="column">
                        <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                            <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                {t("users.user_detail_view.profile")}
                            </Typography>
                            <Button
                                startIcon={
                                    <EditIcon
                                        sx={{
                                            stroke: "#303A8C",
                                            width: "1.125rem",
                                            height: "1.125rem",
                                        }}
                                    />
                                }
                                onClick={() => setUserEditDialogOpened(true)}
                            >
                                {t("users.user_detail_view.edit_details")}
                            </Button>
                        </Stack>
                        <Box padding="1.5rem" bgcolor="white">
                            <UserProfileCard user={user} />
                        </Box>
                    </Stack>
                    <Stack direction="column">
                        <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                            <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                {t("users.user_detail_view.preferences")}
                            </Typography>
                        </Stack>
                        <Box padding="1.5rem" bgcolor="white">
                            <UserPreferencesCard user={user} />
                        </Box>
                    </Stack>
                    {access.canViewUserApiCredentials && (
                        <Stack direction="column">
                            <Stack direction="row" height="4rem" alignItems="center">
                                <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                    {t("users.user_detail_view.api_credentials")}
                                </Typography>
                            </Stack>
                            <Box padding="1.5rem" bgcolor="white">
                                <ApiCredentialsCard
                                    isLoading={isResourceLoading(credentialsManagement.resource)}
                                    credentials={credentials}
                                    onRotateCredentials={onRotateCredentials}
                                />
                            </Box>
                        </Stack>
                    )}
                </Stack>
                {userEditDialogOpened && user !== undefined && (
                    <UserEditDialog
                        mode="edit"
                        user={user}
                        open={userEditDialogOpened}
                        onClose={onUserEditDialogClose}
                    />
                )}
            </>
        );
    } else {
        return null;
    }
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canViewUserApiCredentials"]);
